import React, { useEffect, useState } from "react";
import { superadminAPI } from "../../../api/superadminClient";
import { PAYMENT_STATUS_OPTIONS, formatDate, formatInr } from "./shared";

const CYCLE_LABELS = { 1: "mo", 3: "qtr", 6: "6mo", 12: "yr" };

// Compact one-liner so the negotiated amounts are visible in the row itself,
// not just as a badge that says "something is different here".
function describeContract(pricing) {
  if (!pricing) return "";
  const terms = pricing.cycles
    .map((m) => `${formatInr(pricing.amounts[m])}/${CYCLE_LABELS[m] || `${m}mo`}`)
    .join("  ·  ");
  const cap = pricing.includedUsers ? ` · up to ${pricing.includedUsers} users` : "";
  return `${terms}${cap}`;
}

function contractTooltip(pricing) {
  if (!pricing) return "";
  return pricing.note
    ? `Negotiated contract — ${pricing.note}`
    : "Negotiated contract";
}

// Tenant billing administration. `reloadKey` lets sibling panels (e.g. after a
// tenant is pre-provisioned in Pricing) force a refresh without a page reload.
export default function TenantsPanel({ onError, reloadKey = 0 }) {
  const [tenants, setTenants] = useState([]);
  const [tenantEdits, setTenantEdits] = useState({});
  const [savingTenantId, setSavingTenantId] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [paymentHistories, setPaymentHistories] = useState({});
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await superadminAPI.getTenants();
      const list = resp.data || [];
      setTenants(list);
      const edits = {};
      list.forEach((tenant) => {
        edits[tenant.id] = {
          planType: tenant.planType || "TRIAL",
          planDurationMonths: tenant.planDurationMonths || 1,
          paymentStatus: tenant.paymentStatus || "PENDING",
          nextPaymentDue: tenant.nextPaymentDue
            ? new Date(tenant.nextPaymentDue).toISOString().slice(0, 10)
            : "",
          isRefundEligible: !!tenant.isRefundEligible,
          totalUsersRegistered: tenant.totalUsersRegistered ?? 1,
          alertRecipientLimit: tenant.alertRecipientLimit ?? 1,
          enabledModules: Array.isArray(tenant.enabledModules)
            ? tenant.enabledModules
            : ["po", "inventory"],
        };
      });
      setTenantEdits(edits);
    } catch (err) {
      onError(err.response?.data?.message || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const handleTenantEdit = (tenantId, key, value) => {
    setTenantEdits((prev) => ({
      ...prev,
      [tenantId]: { ...prev[tenantId], [key]: value },
    }));
  };

  const handleToggleModule = (tenantId, moduleKey, checked) => {
    setTenantEdits((prev) => {
      const current = prev[tenantId]?.enabledModules || [];
      const next = checked
        ? [...new Set([...current, moduleKey])]
        : current.filter((key) => key !== moduleKey);
      return {
        ...prev,
        [tenantId]: { ...prev[tenantId], enabledModules: next },
      };
    });
  };

  const handleSaveTenant = async (tenantId) => {
    const payload = tenantEdits[tenantId];
    if (!payload) return;
    setSavingTenantId(tenantId);
    onError("");
    try {
      await superadminAPI.updateTenantBilling(tenantId, payload);
      await loadData();
    } catch (err) {
      onError(err.response?.data?.message || "Failed to update tenant billing");
    } finally {
      setSavingTenantId(null);
    }
  };

  const handleRefund = async (tenantId) => {
    const notes = window.prompt("Optional refund note");
    setSavingTenantId(tenantId);
    onError("");
    try {
      const resp = await superadminAPI.refundTenant(tenantId, notes || "");
      if (resp.data?.razorpayWarning) {
        onError(
          `Refund recorded in DB, but Razorpay warning: ${resp.data.razorpayWarning}`,
        );
      }
      await loadData();
    } catch (err) {
      onError(err.response?.data?.message || "Refund failed");
    } finally {
      setSavingTenantId(null);
    }
  };

  const handleToggleHistory = async (tenantId) => {
    if (expandedHistory === tenantId) {
      setExpandedHistory(null);
      return;
    }
    setExpandedHistory(tenantId);
    if (!paymentHistories[tenantId]) {
      try {
        const resp = await superadminAPI.getPaymentHistory(tenantId);
        setPaymentHistories((prev) => ({ ...prev, [tenantId]: resp.data || [] }));
      } catch (err) {
        setPaymentHistories((prev) => ({ ...prev, [tenantId]: [] }));
      }
    }
  };

  if (loading) return <div className="superadmin-loading">Loading tenants...</div>;

  return (
    <>
    <section className="superadmin-panel">
      <div className="superadmin-panel-head">
        <h2>Tenant List</h2>
        <p>
          No onboarding actions here. Tenant data is seed/system-driven.
        </p>
      </div>
      <div className="superadmin-table-wrap">
        <table className="superadmin-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Plan / Duration</th>
              <th>Payment</th>
              <th>Users Active</th>
              <th>User Seats</th>
              <th>Workflows</th>
              <th>Projects</th>
              <th>Modules</th>
              <th>Next Due</th>
              <th>Refund</th>
              <th>Payments</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => {
              const edit = tenantEdits[tenant.id] || {};
              return (
                <React.Fragment key={tenant.id}>
                  <tr>
                    <td>
                      <strong>{tenant.name}</strong>
                      {tenant.hasCustomPricing && (
                        <span
                          className="superadmin-pill-contract"
                          title={contractTooltip(tenant.customPricing)}
                        >
                          Contract
                        </span>
                      )}
                      <div className="superadmin-subcell">{tenant.slug}</div>
                      {tenant.hasCustomPricing && (
                        <div className="superadmin-subcell superadmin-subcell-contract">
                          {describeContract(tenant.customPricing)}
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        value={edit.planType || "TRIAL"}
                        onChange={(e) =>
                          handleTenantEdit(
                            tenant.id,
                            "planType",
                            e.target.value,
                          )
                        }
                      >
                        <option value="TRIAL">Trial</option>
                        <option value="PRO">Pro</option>
                        <option value="ENTERPRISE">Enterprise</option>
                      </select>
                      {edit.planType === "PRO" && (
                        <select
                          value={edit.planDurationMonths || 1}
                          onChange={(e) =>
                            handleTenantEdit(
                              tenant.id,
                              "planDurationMonths",
                              Number(e.target.value),
                            )
                          }
                        >
                          <option value={1}>1 Month</option>
                          <option value={3}>3 Months</option>
                          <option value={6}>6 Months</option>
                          <option value={12}>1 Year</option>
                        </select>
                      )}
                      <div className="superadmin-subcell">
                        Current: {tenant.planType}
                        {tenant.planDurationMonths > 0
                          ? ` (${tenant.planDurationMonths}m)`
                          : ""}
                      </div>
                    </td>
                    <td>
                      <select
                        value={edit.paymentStatus || "PENDING"}
                        onChange={(e) =>
                          handleTenantEdit(
                            tenant.id,
                            "paymentStatus",
                            e.target.value,
                          )
                        }
                      >
                        {PAYMENT_STATUS_OPTIONS.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{tenant.totalUsersPresent} active</td>
                    <td>
                      <div
                        className="superadmin-subcell"
                        style={{ marginBottom: 4 }}
                      >
                        Now:{" "}
                        <strong>{tenant.totalUsersRegistered}</strong>{" "}
                        seats
                      </div>
                      <div className="superadmin-seats-row">
                        <span className="superadmin-subcell">
                          New seats
                        </span>
                        <input
                          type="number"
                          min={1}
                          value={
                            edit.totalUsersRegistered ??
                            tenant.totalUsersRegistered
                          }
                          onChange={(e) =>
                            handleTenantEdit(
                              tenant.id,
                              "totalUsersRegistered",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                      {tenant.module === "manufacturing" && (
                        <div
                          className="superadmin-seats-row"
                          style={{ marginTop: 6 }}
                          title="How many people get the admin's alerts and daily emails, the admin included"
                        >
                          <span className="superadmin-subcell">
                            Alert recipients
                          </span>
                          <input
                            type="number"
                            min={1}
                            max={50}
                            value={
                              edit.alertRecipientLimit ??
                              tenant.alertRecipientLimit ??
                              1
                            }
                            onChange={(e) =>
                              handleTenantEdit(
                                tenant.id,
                                "alertRecipientLimit",
                                e.target.value === "" ? "" : Number(e.target.value),
                              )
                            }
                          />
                        </div>
                      )}
                    </td>
                    <td>{tenant.totalWorkflows}</td>
                    <td>{tenant.totalProjectsCreated}</td>
                    <td>
                      {tenant.module === "manufacturing" ? (
                        <>
                          <label className="superadmin-inline-check">
                            <input
                              type="checkbox"
                              checked={!!edit.enabledModules?.includes("po")}
                              onChange={(e) =>
                                handleToggleModule(
                                  tenant.id,
                                  "po",
                                  e.target.checked,
                                )
                              }
                            />
                            PO
                          </label>
                          <label className="superadmin-inline-check">
                            <input
                              type="checkbox"
                              checked={!!edit.enabledModules?.includes("inventory")}
                              onChange={(e) =>
                                handleToggleModule(
                                  tenant.id,
                                  "inventory",
                                  e.target.checked,
                                )
                              }
                            />
                            Inventory
                          </label>
                          <label className="superadmin-inline-check">
                            <input
                              type="checkbox"
                              checked={!!edit.enabledModules?.includes("taskManagement")}
                              onChange={(e) =>
                                handleToggleModule(
                                  tenant.id,
                                  "taskManagement",
                                  e.target.checked,
                                )
                              }
                            />
                            Task Management
                          </label>
                          <label className="superadmin-inline-check">
                            <input
                              type="checkbox"
                              checked={!!edit.enabledModules?.includes("assets")}
                              onChange={(e) =>
                                handleToggleModule(
                                  tenant.id,
                                  "assets",
                                  e.target.checked,
                                )
                              }
                            />
                            Asset Management
                          </label>
                        </>
                      ) : (
                        <span className="superadmin-subcell">-</span>
                      )}
                    </td>
                    <td>
                      <input
                        type="date"
                        value={edit.nextPaymentDue || ""}
                        onChange={(e) =>
                          handleTenantEdit(
                            tenant.id,
                            "nextPaymentDue",
                            e.target.value,
                          )
                        }
                      />
                      <div className="superadmin-subcell">
                        Current: {formatDate(tenant.nextPaymentDue)}
                      </div>
                    </td>
                    <td>
                      <label className="superadmin-inline-check">
                        <input
                          type="checkbox"
                          checked={!!edit.isRefundEligible}
                          onChange={(e) =>
                            handleTenantEdit(
                              tenant.id,
                              "isRefundEligible",
                              e.target.checked,
                            )
                          }
                        />
                        Eligible
                      </label>
                      <button
                        className="superadmin-link-btn"
                        disabled={
                          !tenant.isRefundEligible ||
                          savingTenantId === tenant.id
                        }
                        onClick={() => handleRefund(tenant.id)}
                      >
                        Refund
                      </button>
                    </td>
                    <td>
                      <button
                        className="superadmin-link-btn"
                        style={{
                          background: "#e0f2fe",
                          color: "#075985",
                        }}
                        onClick={() => handleToggleHistory(tenant.id)}
                      >
                        {expandedHistory === tenant.id ? "Hide" : "View"}
                      </button>
                    </td>
                    <td>
                      <button
                        onClick={() => handleSaveTenant(tenant.id)}
                        disabled={savingTenantId === tenant.id}
                      >
                        {savingTenantId === tenant.id
                          ? "Saving..."
                          : "Save"}
                      </button>
                    </td>
                  </tr>
                  {expandedHistory === tenant.id && (
                    <tr>
                      <td
                        colSpan={12}
                        style={{
                          background: "#f8fbff",
                          padding: "10px 12px",
                        }}
                      >
                        <strong
                          style={{ fontSize: 12, color: "#0f2d5c" }}
                        >
                          Payment History
                        </strong>
                        {!paymentHistories[tenant.id] ? (
                          <div className="superadmin-subcell">
                            Loading...
                          </div>
                        ) : paymentHistories[tenant.id].length === 0 ? (
                          <div className="superadmin-subcell">
                            No payment records found.
                          </div>
                        ) : (
                          <table
                            className="superadmin-table"
                            style={{ marginTop: 6 }}
                          >
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Plan</th>
                                <th>Duration</th>
                                <th>Users</th>
                                <th>Amount</th>
                                <th>Notes</th>
                                <th>Razorpay Payment ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paymentHistories[tenant.id].map(
                                (record) => (
                                  <tr key={record.id}>
                                    <td>{formatDate(record.paidAt)}</td>
                                    <td>{record.planType}</td>
                                    <td>
                                      {record.durationMonths > 0
                                        ? `${record.durationMonths}m`
                                        : "-"}
                                    </td>
                                    <td>{record.totalUsers}</td>
                                    <td>
                                      {formatInr(
                                        record.amountPaise / 100,
                                      )}
                                    </td>
                                    <td>{record.notes || "-"}</td>
                                    <td className="superadmin-subcell">
                                      {record.razorpayPaymentId || "-"}
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>

    </>
  );
}
